import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { usePrices } from "@/lib/prices-context";
import { ENDPOINTS, ORACLE_CONTRACT_ID, STELLAR_EXPERT, type HealthResponse } from "@/lib/api";
import { formatUptime } from "@/lib/format";

/**
 * Live trust + honesty. Bloomberg-grid metric cards driven by /health,
 * followed by a v0/v1 disclosure card. Every number is fetched live so
 * "trust us" is replaced by "verify us right now."
 */
export function TrustDashboard() {
  const { assets } = usePrices();
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [reachable, setReachable] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    async function poll() {
      try {
        const res = await fetch(ENDPOINTS.health, { cache: "no-store" });
        const body = (await res.json()) as HealthResponse;
        if (!cancelled) {
          setHealth(body);
          setReachable(res.ok);
        }
      } catch {
        if (!cancelled) setReachable(false);
      }
    }
    poll();
    const id = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const assetCount = assets ? Object.keys(assets).length : null;

  return (
    <section
      className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-24 pb-12 sm:px-10 sm:pt-32"
      aria-labelledby="trust-title"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-m45">
        <span className="text-emerald">●</span> LIVE TRUST
      </p>
      <h2
        id="trust-title"
        className="mt-6 font-serif text-3xl leading-[1.05] tracking-[-0.012em] text-text sm:text-5xl md:text-6xl"
      >
        <em className="italic text-emerald">Every number</em> below is fetched live.
      </h2>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-m70">
        Pulled from <code className="font-mono text-text">api.noeracle.org/health</code>{" "}
        and <code className="font-mono text-text">/v1/latest</code> every 5 seconds.
        On-chain contract links go to Stellar.expert.
      </p>

      <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <Metric
          label="STATUS"
          value={
            !reachable
              ? "UNREACHABLE"
              : health?.status === "ok"
              ? "HEALTHY"
              : (health?.status ?? "…").toUpperCase()
          }
          tone={
            !reachable
              ? "bad"
              : health?.status === "ok"
              ? "ok"
              : "warn"
          }
        />
        <Metric
          label="UPTIME"
          value={formatUptime(health?.uptime_s)}
        />
        <Metric
          label="LAST SIGNED"
          value={
            health?.last_signed_age_s != null
              ? `${health.last_signed_age_s}s ago`
              : "—"
          }
          tone={
            health?.last_signed_age_s == null
              ? undefined
              : health.last_signed_age_s <= 2
              ? "ok"
              : health.last_signed_age_s <= 5
              ? "warn"
              : "bad"
          }
        />
        <Metric
          label="ASSETS LIVE"
          value={assetCount != null ? `${assetCount} feeds` : "—"}
        />
        <Metric label="CADENCE" value="500 ms" tone="ok" />
        <Metric label="SIGNATURE" value="Ed25519" />
        <Metric label="NETWORK" value="Stellar · Testnet" tone="warn" toneOnLabelOnly />
        <Metric label="PUBLISHERS" value="1 · self-operated" />
      </div>

      {/* Contract address — full row */}
      <div className="mt-3 rounded-xl border border-border bg-surface/40 px-5 py-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-m45">
          ORACLE CONTRACT · TESTNET
        </div>
        <a
          href={STELLAR_EXPERT.contract(ORACLE_CONTRACT_ID)}
          target="_blank"
          rel="noopener"
          className="mt-1.5 inline-flex flex-wrap items-center gap-2 font-mono text-[13px] tabular text-text break-all hover:text-emerald transition-colors"
        >
          {ORACLE_CONTRACT_ID}
          <ExternalLink className="size-3 text-m45" />
        </a>
      </div>

      {/* Honesty section */}
      <div className="mt-12 rounded-xl border border-amber/30 bg-amber/[0.03] p-6 sm:p-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber">
          ● WHAT YOU SHOULD KNOW
        </p>
        <h3 className="mt-4 font-serif text-2xl leading-tight text-text sm:text-3xl">
          <em className="italic">v0</em> runs one self-operated signer.{" "}
          <em className="italic">v1</em> brings the threshold.
        </h3>
        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-10">
          <p className="text-[14.5px] leading-relaxed text-m70">
            Noeracle today is a testnet prototype with a single self-operated
            attestation signer. It is suitable for hackathons, integration
            testing, and shape-checking your contract's pull path. It is{" "}
            <span className="text-text">not yet suitable for production capital.</span>
          </p>
          <p className="text-[14.5px] leading-relaxed text-m70">
            v1 introduces multi-publisher threshold signing — a quorum of
            independent operators must co-sign each round before a price is
            considered valid. Coupled with an independent audit, this is the
            target shape for mainnet. Roadmap and threat model live in the docs.
          </p>
        </div>
      </div>
    </section>
  );
}

function Metric({
  label,
  value,
  tone,
  toneOnLabelOnly,
}: {
  label: string;
  value: string;
  tone?: "ok" | "warn" | "bad";
  toneOnLabelOnly?: boolean;
}) {
  const valueColor =
    toneOnLabelOnly
      ? "text-text"
      : tone === "ok"
      ? "text-emerald"
      : tone === "warn"
      ? "text-amber"
      : tone === "bad"
      ? "text-red"
      : "text-text";
  const labelColor =
    toneOnLabelOnly && tone === "warn" ? "text-amber" : "text-m45";

  return (
    <div className="rounded-xl border border-border bg-surface/40 px-4 py-4">
      <div
        className={`font-mono text-[9.5px] uppercase tracking-[0.24em] ${labelColor}`}
      >
        {label}
      </div>
      <div
        className={`mt-1.5 font-mono text-[15px] tabular leading-none ${valueColor}`}
      >
        {value}
      </div>
    </div>
  );
}
